'use client';

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { CommerceImage } from '@akademi/site-builder-runtime';
import {
  PanelArt,
  PHOTO_PLACEHOLDER_ART,
  PHOTO_PLACEHOLDER_TONES,
  photoPlaceholderHash,
} from './photoPlaceholderArt';

/**
 * Product image cell, shared by {@code ProductGrid}, {@code ProductDetail}
 * and {@code KitEnquiryCta}.
 *
 * <p>Honours {@link CommerceImage.alt}'s contract precisely: an empty
 * string is not "no alt text yet" — it is the backend telling us this image
 * is decorative, so it renders as a plain CSS background on a `<div
 * aria-hidden>`, never an `<img alt="">`. A non-empty `alt` renders a real
 * `<img>` so it reaches assistive tech as content.
 *
 * <p>No image at all is a different case again, and common: supplier feeds carry part
 * numbers and datasheets, not photographs. A bare empty box reads as a page that failed
 * to load, so this says the photograph is coming — which is true, where a stock image
 * would be a claim about the part that nobody has checked.
 */
export function ProductThumb({
  image,
  aspect = '4 / 3',
  style,
  sizes,
  seed,
}: {
  image?: CommerceImage;
  aspect?: string;
  style?: CSSProperties;
  /** Passed straight to `<img sizes>` for a grid of many thumbs. */
  sizes?: string;
  /** Stable per product — its code, path or id. Picks the no-photograph plate's motif. */
  seed?: string;
}) {
  const box: CSSProperties = {
    aspectRatio: aspect,
    borderRadius: 'var(--radius-md, 8px)',
    overflow: 'hidden',
    background: 'var(--color-surface-muted, #f4f4f5)',
    ...style,
  };

  if (!image?.url) {
    return <PhotoComingSoon box={box} seed={seed} />;
  }

  if (!image.alt) {
    return (
      <div
        aria-hidden
        style={{
          ...box,
          // JSON.stringify quotes + escapes the URL for safe embedding in a
          // CSS url(...) token (both use backslash-escaped double quotes).
          backgroundImage: `url(${JSON.stringify(image.url)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
    );
  }

  return (
    <div style={box}>
      <img
        src={image.url}
        alt={image.alt}
        loading="lazy"
        decoding="async"
        sizes={sizes}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  );
}

/**
 * The no-photograph state: an object on white, on the same box the image would fill.
 *
 * <p>It imitates the SHAPE of catalogue photography rather than any product. A grid of
 * identical grey rectangles shows nobody what the shop looks like once photographs land, so
 * the tone and motif are picked from the product's own code: the grid keeps the rhythm real
 * photography would give it while every plate is plainly an abstract shape. Chosen by hash,
 * never at random, so the plate survives hydration and does not flicker between renders.
 */
function PhotoComingSoon({ box, seed }: { box: CSSProperties; seed?: string }) {
  const { t } = useTranslation('site-builder-blocks');
  const label = t('commerce.photoComingSoon');
  const key = seed ?? '';
  // `?? fallback` because the index signature is checked: the modulo cannot miss, but the
  // compiler cannot know that and a bare assertion would hide a real slip if the lists change.
  const tone =
    PHOTO_PLACEHOLDER_TONES[photoPlaceholderHash(key, 'tone') % PHOTO_PLACEHOLDER_TONES.length] ??
    '#475569';
  const Art =
    PHOTO_PLACEHOLDER_ART[photoPlaceholderHash(key, 'art') % PHOTO_PLACEHOLDER_ART.length] ??
    PanelArt;
  return (
    <div
      role="img"
      aria-label={label}
      style={{
        ...box,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        background:
          'radial-gradient(120% 80% at 50% 38%, var(--color-surface-card, #fff) 40%, color-mix(in srgb, var(--color-text-primary, #0f172a) 7%, transparent) 100%)',
      }}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 120 120"
        style={{ width: '60%', maxWidth: 160, color: tone }}
      >
        <Art />
      </svg>
      <span
        style={{
          fontSize: 11,
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          textAlign: 'center',
          padding: '0 8px',
          color: 'var(--color-text-muted, #71717a)',
        }}
      >
        {label}
      </span>
    </div>
  );
}
